<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    :closeOnScrim="false"
    placement="center"
    wide
  >
    <template #header>
      <div class="header">
        <div class="header-title">
          <span class="header-icon" aria-hidden="true">{{ ICONS.states.danger }}</span>
          <div style="min-width: 0;">
            <div class="header-text">You Died</div>
            <div class="text-muted header-sub">{{ subtitle }}</div>
          </div>
        </div>
      </div>
    </template>

    <UiPanel tone="combat">
      <UiSectionHeader>
        <template #title>What Happened</template>
      </UiSectionHeader>
      <div class="kv-grid">
        <div class="kv">
          <div class="kv-label">Killed by</div>
          <div class="kv-value">{{ killedBy }}</div>
        </div>
        <div class="kv">
          <div class="kv-label">Location</div>
          <div class="kv-value">{{ locationLabel }}</div>
        </div>
        <div class="kv">
          <div class="kv-label">Loot timing</div>
          <div class="kv-value">Private 10:00, then public 5:00</div>
        </div>
      </div>
    </UiPanel>

    <UiPanel tone="combat">
      <UiSectionHeader>
        <template #title>Dropped</template>
        <template #actions>
          <UiChip variant="muted" class="stat-mono num">{{ formatNumber(totalStacks) }} stacks</UiChip>
          <UiChip v-if="goldDropped > 0" variant="muted" class="stat-mono num">{{ formatNumber(goldDropped) }} gp</UiChip>
        </template>
      </UiSectionHeader>

      <div class="drops">
        <div v-for="drop in drops" :key="drop.itemId" class="drop-row">
          <div class="drop-name">{{ drop.itemName || drop.itemId }}</div>
          <div class="drop-qty stat-mono num">×{{ formatNumber(drop.qty) }}</div>
        </div>
      </div>
    </UiPanel>

    <div class="row wrap" style="justify-content: flex-end;">
      <UiButton size="sm" variant="secondary" :disabled="busy" @click="acknowledge">
        <template #icon>{{ ICONS.states.active }}</template>
        Acknowledge
      </UiButton>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DeathEvent } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const props = defineProps<{
  overlayZIndex: number;
  deathEvent: DeathEvent;
  onAcknowledge: () => Promise<void> | void;
}>();

const emit = defineEmits<{ close: [] }>();
const busy = ref(false);

const subtitle = computed(() => "Review the drop summary, then acknowledge to continue.");
const killedBy = computed(() => props.deathEvent.killedByName || "Unknown");
const locationLabel = computed(() => {
  const area = props.deathEvent.deathAreaName || props.deathEvent.deathAreaId;
  const loc = props.deathEvent.deathLocationName ? ` — ${props.deathEvent.deathLocationName}` : "";
  return `${area}${loc}`;
});
const drops = computed(() => props.deathEvent?.drops ?? []);
const totalStacks = computed(() => drops.value.length);
const goldDropped = computed(() => drops.value.find((d) => d.itemId === "item-gold")?.qty ?? 0);

async function acknowledge() {
  if (busy.value) return;
  busy.value = true;
  try {
    await props.onAcknowledge();
    emit("close");
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.header-title {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  min-width: 0;
}

.header-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-2);
  display: grid;
  place-items: center;
  background: var(--surface-ghost-bg);
  border: 1px solid color-mix(in srgb, var(--accent-combat) 18%, var(--border-0));
  box-shadow: var(--shadow-0);
  font-size: 1.25rem;
}

.header-text {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  line-height: 1.1;
}

.header-sub {
  margin-top: 0.2rem;
  font-size: 0.9rem;
}

.kv-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: var(--space-sm);
}

.kv {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.65rem 0.75rem;
}

.kv-label {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  font-weight: 750;
  margin-top: 0.25rem;
}

.drops {
  margin-top: var(--space-sm);
  display: grid;
  gap: 0.55rem;
  max-height: 280px;
  overflow: auto;
}

.drop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.6rem 0.75rem;
}

.drop-name {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drop-qty {
  flex: 0 0 auto;
}
</style>
